import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField,
  Grid
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import { SaveAlt as ExportIcon } from "@mui/icons-material";
import PrimaryButton from "../../../components/Button"; // ✅ Check path
import { useNavigate } from "react-router-dom"; // ✅ Fix for navigate

interface JournalLine {
  account: string;
  debit: number;
  credit: number;
  description?: string;
}

interface JournalEntry {
  _id: string;
  date: string;
  module: string;
  refId: string;
  description: string;
  lines: JournalLine[];
}

export default function JournalsPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const navigate = useNavigate(); // ✅ define navigate
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filtered, setFiltered] = useState<JournalEntry[]>([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [poId, setPoId] = useState("");

  const fetchEntries = useCallback(async (params?: Record<string,string>) => {
    try {
      const res = await api.get("/journals", {
        headers: { Authorization: `Bearer ${token}` },
        params: params || undefined,
      });
      setEntries(res.data);
      setFiltered(res.data);
    } catch (err) {
      console.error("Error fetching journal entries", err);
    }
  }, [token]);

  useEffect(() => { if (token) fetchEntries(); }, [token, fetchEntries]);

  const handleFilter = async () => {
    // Use server-side filtering for supplier/PO, also pass module/search
    const params: Record<string,string> = {};
    if (supplierId.trim()) params.supplierId = supplierId.trim();
    if (poId.trim()) params.poId = poId.trim();
    if (moduleFilter.trim()) params.module = moduleFilter.trim();
    if (search.trim()) params.search = search.trim();

    await fetchEntries(params);
  };

  const handleExport = (type: "csv" | "pdf") => {
    alert(`Export as ${type.toUpperCase()} coming soon.`);
  };

  return (
    <Box p={4}>
      {/* Header */}
      <Box className="flex justify-between items-center mb-4">
        <Typography variant="h5" fontWeight="bold">Journal Entries</Typography>

        {role === "admin" && (
          <PrimaryButton
            label="Add Manual Entry"
            onClick={() => navigate("/accounts/journals/new")}
          />
        )}
      </Box>

      {/* Filters */}
      <Grid container spacing={2} className="mb-4">
        <Grid item xs={12} sm={4}>
          <Select
            fullWidth
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Modules</MenuItem>
            <MenuItem value="POS">POS</MenuItem>
            <MenuItem value="Invoice">Invoice</MenuItem>
            <MenuItem value="Payroll">Payroll</MenuItem>
            <MenuItem value="Purchase">Purchase</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Search"
            placeholder="Ref ID or Description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleFilter()}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Supplier ID"
            placeholder="supplierId"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleFilter()}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="PO ID"
            placeholder="poId"
            value={poId}
            onChange={(e) => setPoId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleFilter()}
          />
        </Grid>
        <Grid item xs={12} className="flex gap-2">
          <Button variant="outlined" onClick={handleFilter}>Apply</Button>
          <Button
            variant="outlined"
            onClick={() => {
              setModuleFilter("");
              setSearch("");
              setSupplierId("");
              setPoId("");
              setFiltered(entries);
            }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {/* Export Buttons */}
      <Box className="flex gap-3 mb-2">
        <Button variant="text" startIcon={<ExportIcon />} onClick={() => handleExport("csv")}>
          Export CSV
        </Button>
        <Button variant="text" startIcon={<ExportIcon />} onClick={() => handleExport("pdf")}>
          Export PDF
        </Button>
      </Box>

      {/* Journal Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Lines</TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
              {filtered.map((entry) => {
                const amount = (entry.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);
                return (
                  <TableRow key={entry._id}>
                    <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                    <TableCell>{entry.module}</TableCell>
                    <TableCell>{entry.refId}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell align="right">${amount.toFixed(2)}</TableCell>
                    <TableCell align="right">{entry.lines.length}</TableCell>
                  </TableRow>
                );
              })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No journal entries found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
 
